import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { collaborationAPI } from '../../services/api';
import { Collaboration, CollaborationApplication } from '../../types';
import { incrementCollaborationCount } from './authSlice';
import toast from 'react-hot-toast';


// Define the slice state interface
interface CollaborationState {
  collaborations: Collaboration[];               // All collaborations (e.g., public or searched)
  userCollaborations: Collaboration[];           // Collaborations of the profile user
  selectedCollaboration: Collaboration | null;   // Selected collaboration for detail modal
  applications: CollaborationApplication[];      // Applications related to collaboration
  loading: boolean;
  error: string | null;
}

const initialState: CollaborationState = {
  collaborations: [],
  userCollaborations: [],
  selectedCollaboration: null,
  applications: [],
  loading: false,
  error: null,
};

// Async thunks

export const fetchCollaborations = createAsyncThunk(
    'collaboration/fetchAll',
    async (params: any = {}, { rejectWithValue }) => { // Fix: provide default value instead of optional
      try {
        const response = await collaborationAPI.getAll(params);
        return response.data.data as Collaboration[];
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to fetch collaborations');
      }
    }
  );
  
// Fix: Make userId required (it should be required anyway)
export const fetchUserCollaborations = createAsyncThunk(
    'collaboration/fetchUser',
    async (userId: string, { rejectWithValue }) => { // Fix: removed optional parameter
      try {
        const response = await collaborationAPI.getByUser(userId);
        return response.data.data as Collaboration[];
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to fetch user collaborations');
      }
    }
  );
  
  // Fix: Make formData required (it should be required anyway)
  export const createCollaboration = createAsyncThunk(
    'collaboration/create',
    async (payload: { formData: FormData, onUploadProgress?: (progressEvent: any) => void }, { dispatch, rejectWithValue }) => {
      try {
        const response = await collaborationAPI.createCollaboration(payload.formData, payload.onUploadProgress);
        
        // Sync with auth state
        dispatch(incrementCollaborationCount());
        
        // Return collaboration data and reward_unlocked if present
        const result = response.data.data as Collaboration;
        (result as any).__reward_unlocked = response.data.reward_unlocked || null;
        return result;
      } catch (error: any) {
        // console.log("error", error)
        return rejectWithValue(error.response?.data?.error || 'Failed to create collaboration');
      }
    }
  );
  
  // Fix: Make parameters required
  export const updateCollaboration = createAsyncThunk(
    'collaboration/update',
    async (payload: { id: string; formData: FormData; onUploadProgress?: (progressEvent: any) => void }, { rejectWithValue }) => { // Fix: combined into single required parameter
      try {
        const response = await collaborationAPI.updateCollaboration(payload.id, payload.formData, payload.onUploadProgress);
        return response.data.data as Collaboration;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to update collaboration');
      }
    }
  );
  
  // Fix: Make id required
  export const deleteCollaboration = createAsyncThunk(
    'collaboration/delete',
    async (id: string, { rejectWithValue }) => { // Fix: removed optional parameter
      try {
        await collaborationAPI.delete(id);
        return id;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to delete collaboration');
      }
    }
  );
  
  // Fix: Make data required
  export const applyToCollaboration = createAsyncThunk(
    'collaboration/apply',
    async (
      data: { collaboration_id: string; message: string; portfolio_items?: string[] },
      { rejectWithValue }
    ) => { // Fix: removed optional parameter
      try {
        const response = await collaborationAPI.apply(data);
        return response.data.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to apply');
      }
    }
  );
  
  // Fix: Make collaborationId required
  export const fetchApplications = createAsyncThunk(
    'collaboration/applications',
    async (collaborationId: string, { rejectWithValue }) => { // Fix: removed optional parameter
      try {
        const response = await collaborationAPI.getApplications(collaborationId);
        return response.data.data as CollaborationApplication[];
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to fetch applications');
      }
    }
  );

  export const fetchCollaborationById = createAsyncThunk(
    'collaboration/fetchById',
    async (id: string, { rejectWithValue }) => {
      try {
        const response = await collaborationAPI.getCollaboration(id);
        return response.data.data as Collaboration;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to fetch collaboration');
      }
    }
  );

const collaborationSlice = createSlice({
  name: 'collaboration',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    resetSelectedCollaboration(state) {
      state.selectedCollaboration = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCollaborations.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCollaborations.fulfilled, (state, action) => {
        state.collaborations = action.payload;
        state.loading = false;
      })
      .addCase(fetchCollaborations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchUserCollaborations.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserCollaborations.fulfilled, (state, action) => {
        state.userCollaborations = action.payload ?? [];
        state.loading = false;
      })
      .addCase(fetchUserCollaborations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(createCollaboration.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCollaboration.fulfilled, (state, action) => {
        state.collaborations.unshift(action.payload);
        state.userCollaborations.unshift(action.payload);
        state.loading = false;
      })
      .addCase(createCollaboration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string);
      })

      .addCase(updateCollaboration.fulfilled, (state, action) => {
        const idx = state.collaborations.findIndex(c => c.id === action.payload.id);
        if (idx > -1) state.collaborations[idx] = action.payload;
        const uidx = state.userCollaborations.findIndex(c => c.id === action.payload.id);
        if (uidx > -1) state.userCollaborations[uidx] = action.payload;
        // toast.success('Collaboration updated successfully!');
      })

      .addCase(deleteCollaboration.fulfilled, (state, action) => {
        state.collaborations = state.collaborations.filter(c => c.id !== action.payload);
        state.userCollaborations = state.userCollaborations.filter(c => c.id !== action.payload);
        toast.success('Collaboration deleted successfully!');
      })

      .addCase(applyToCollaboration.fulfilled, state => {
        toast.success('Applied to collaboration successfully!');
      })

      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.applications = action.payload;
      })
      
      .addCase(fetchCollaborationById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCollaborationById.fulfilled, (state, action) => {
        state.selectedCollaboration = action.payload;
        state.loading = false;
      })
      .addCase(fetchCollaborationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearError, resetSelectedCollaboration } = collaborationSlice.actions;

export default collaborationSlice.reducer;